import { useEffect, useMemo, useState } from "react";
import { Copy, MoreHorizontal, RefreshCw, Video } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ProductEditPageProduct } from "@/routes/productEditTypes";
import { MeetingFormatEnum, resolveMeetingFormat } from "@/types/MeetingFormat";
import { getPageBaseUrl } from "@/routes/pages/edit/pageEditorUtils";
import { toast } from "sonner";

function formatDateForInput(dateString?: string) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

export function ProductEditMeetingTab({
  productId,
  product,
}: {
  productId: string;
  product: ProductEditPageProduct;
}) {
  const [isJoining, setIsJoining] = useState(false);
  const [isRecreating, setIsRecreating] = useState(false);
  const [isSavingFormat, setIsSavingFormat] = useState(false);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [meetingFormat, setMeetingFormat] = useState(() =>
    resolveMeetingFormat(product.meeting?.meetingFormat),
  );
  const [meetingId, setMeetingId] = useState(
    product.meeting?.realtimeKit?.meetingId ?? "",
  );
  const [meetingStatus, setMeetingStatus] = useState(
    product.meeting?.realtimeKit?.status ?? "",
  );
  const [customDomainHostname, setCustomDomainHostname] = useState<string | null>(null);
  const [meetingStart, setMeetingStart] = useState(() =>
    formatDateForInput(product.meeting?.start),
  );
  const [meetingEnd, setMeetingEnd] = useState(() =>
    formatDateForInput(product.meeting?.end),
  );
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    fetch("/api/session")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const artistName = data?.artist?.name?.trim();
        if (artistName) {
          setDisplayName(artistName);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch("/api/custom-domains")
      .then((res) => res.json())
      .then((data) => {
        const domains = data.customDomains || [];
        setCustomDomainHostname(domains[0]?.hostname ?? null);
      })
      .catch(() => {});
  }, []);

  const meetingUrl = useMemo(
    () => `${getPageBaseUrl(customDomainHostname)}/meetings/${productId}`,
    [customDomainHostname, productId],
  );

  useEffect(() => {
    setMeetingId(product.meeting?.realtimeKit?.meetingId ?? "");
    setMeetingStatus(product.meeting?.realtimeKit?.status ?? "");
    setMeetingFormat(resolveMeetingFormat(product.meeting?.meetingFormat));
    setMeetingStart(formatDateForInput(product.meeting?.start));
    setMeetingEnd(formatDateForInput(product.meeting?.end));
  }, [
    product.meeting?.realtimeKit?.meetingId,
    product.meeting?.realtimeKit?.status,
    product.meeting?.meetingFormat,
    product.meeting?.start,
    product.meeting?.end,
  ]);

  const handleScheduleSave = async ({
    start,
    end,
  }: {
    start: string;
    end: string;
  }) => {
    setIsSavingSchedule(true);
    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          meeting: {
            start: start ? new Date(start).toISOString() : null,
            end: end ? new Date(end).toISOString() : null,
          },
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || "Failed to update livestream schedule");
      }
    } catch (error) {
      console.error("Failed to update livestream schedule:", error);
      alert("An error occurred while saving");
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleMeetingFormatChange = async (value: MeetingFormatEnum) => {
    const previous = meetingFormat;
    setMeetingFormat(value);
    setIsSavingFormat(true);
    try {
      const response = await fetch(`/api/products/${productId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ meeting: { meetingFormat: value } }),
      });
      const data = await response.json();
      if (!response.ok) {
        setMeetingFormat(previous);
        alert(data.error || "Failed to update meeting format");
      }
    } catch (error) {
      console.error("Failed to update meetingFormat:", error);
      setMeetingFormat(previous);
      alert("An error occurred while saving");
    } finally {
      setIsSavingFormat(false);
    }
  };

  const handleRecreate = async () => {
    const confirmed = window.confirm(
      "Recreate this meeting? The current meeting ID will stop working and a new one will be created."
    );
    if (!confirmed) return;

    setIsRecreating(true);
    try {
      const response = await fetch(`/api/products/${productId}/meeting/recreate`, {
        method: "POST",
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || "Failed to recreate meeting");
        return;
      }
      if (data.meeting?.realtimeKit) {
        setMeetingId(data.meeting.realtimeKit.meetingId ?? "");
        setMeetingStatus(data.meeting.realtimeKit.status ?? "");
      }
    } catch (error) {
      console.error("Failed to recreate meeting:", error);
      alert("An error occurred while recreating the meeting");
    } finally {
      setIsRecreating(false);
    }
  };

  const handleJoin = async () => {
    setIsJoining(true);
    try {
      const response = await fetch(`/api/products/${productId}/meeting/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          role: "host",
          name: displayName.trim() || undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        alert(data.error || "Failed to get meeting join URL");
        return;
      }
      if (data.joinUrl) {
        window.open(data.joinUrl, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Failed to join meeting:", error);
      alert("An error occurred while joining the meeting");
    } finally {
      setIsJoining(false);
    }
  };

  const handleCopyMeetingUrl = async () => {
    await navigator.clipboard.writeText(meetingUrl);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="meetingFormat">Meeting format</Label>
        <Select
          value={meetingFormat}
          onValueChange={(value) => void handleMeetingFormatChange(value as MeetingFormatEnum)}
          disabled={isSavingFormat}
        >
          <SelectTrigger id="meetingFormat">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={MeetingFormatEnum.WEBINAR}>Webinar</SelectItem>
            <SelectItem value={MeetingFormatEnum.LIVESTREAM}>Livestream</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {meetingFormat === MeetingFormatEnum.LIVESTREAM ? (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="space-y-1">
            <Label>Livestream schedule</Label>
            <p className="text-sm text-muted-foreground">
              Viewers see a waiting screen before the start time and after the end time.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="meetingStart">Start</Label>
              <Input
                id="meetingStart"
                type="datetime-local"
                value={meetingStart}
                onChange={(event) => setMeetingStart(event.target.value)}
                disabled={isSavingSchedule}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meetingEnd">End</Label>
              <Input
                id="meetingEnd"
                type="datetime-local"
                value={meetingEnd}
                onChange={(event) => setMeetingEnd(event.target.value)}
                disabled={isSavingSchedule}
              />
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={isSavingSchedule}
            onClick={() =>
              void handleScheduleSave({ start: meetingStart, end: meetingEnd })
            }
          >
            {isSavingSchedule ? "Saving..." : "Save schedule"}
          </Button>
        </div>
      ) : null}
      <div className="space-y-2">
        <Label htmlFor="meetingUrl">Meeting URL</Label>
        <div className="flex gap-2">
          <Input
            id="meetingUrl"
            value={meetingUrl}
            readOnly
            disabled
            className="flex-1 font-mono text-sm"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0"
            aria-label="Copy meeting URL"
            onClick={() => void handleCopyMeetingUrl()}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="meetingId">Meeting ID</Label>
        <div className="flex gap-2">
          <Input
            id="meetingId"
            value={meetingId}
            readOnly
            disabled
            className="flex-1"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                aria-label="Meeting actions"
                disabled={isJoining || isRecreating}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={handleRecreate}
                disabled={isJoining || isRecreating}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {isRecreating ? "Recreating..." : "Recreate Meeting"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="meetingStatus">Status</Label>
        <Input
          id="meetingStatus"
          value={meetingStatus}
          readOnly
          disabled
          placeholder="—"
        />
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="space-y-2">
          <Label htmlFor="hostDisplayName">Display name</Label>
          <Input
            id="hostDisplayName"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            disabled={isJoining || isRecreating}
            placeholder="Display name"
            className="w-48"
          />
        </div>
        <Button
          type="button"
          disabled={isJoining || isRecreating || !meetingId || !displayName.trim()}
          onClick={() => void handleJoin()}
        >
          <Video className="h-4 w-4 mr-2" />
          {isJoining ? "Joining..." : "Join as host"}
        </Button>
      </div>
    </div>
  );
}
